import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Animated,
  Easing,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import {
  AntDesign,
  Entypo,
  Ionicons,
  MaterialIcons,
  Octicons,
} from "@expo/vector-icons";

import Colors from "../../../Resources/Color";
import Fonts from "../../../Resources/Fonts";
import CustomAlert from "../../Components/CustomAlert";
import IconEmptyView from "../../Components/IconEmptyView";
import FloatingMenu from "../../Components/FloatingMenu";
import HoverableTouchableOpacity from "../../Components/HoverableTouchableOpacity";
import { useToast } from "../../contexts/ToastContext";
import useChevronRotation from "../../hooks/useChevronRotation";
import { useDevice, useScreenDimensions } from "../../_layout";
import { NutritionPlanEditorView } from "./editor/NutritionPlanEditorView";
import { NutritionPlanAssignmentModal } from "./NutritionPlanAssignmentModal";
import { formatNutritionPlanUpdatedDate } from "./nutritionPlanDateUtils";
import { buildNutritionPlanDuplicateDraft } from "./nutritionPlanLogic";
import { getNutritionPlanDisplayName } from "./nutritionPlanNameUtils";
import {
  deriveVisibleNutritionPlans,
  NUTRITION_PLAN_SORT_KEYS,
} from "./nutritionPlanSearch";
import {
  flattenPendingNutritionPlansFromEntries,
  getPendingNutritionPlansTotalCount,
  removePendingNutritionPlanFromWorkspaceSummaries,
  selectPendingNutritionPlanEntries,
  shouldShowPendingNutritionPlansSection,
} from "./pendingNutritionPlanDiscoverabilityUtils";
import { shouldDisablePendingNutritionMenuRowInteractions } from "./pendingNutritionPlanActions";
import {
  getNutritionPlanLibraryCardLayout,
  resolvePendingNutritionPlanRowUiState,
} from "./nutritionPlanLibraryViewUtils";
import { createCoachNutritionPlanSeed } from "../shared/nutritionPlanSeed";
import {
  formatCalorieValue,
  formatMacroValue,
  getUiMacroShortLabel,
  UI_MACRO_DISPLAY_ORDER,
} from "../shared/nutritionFormatters";
import {
  getSearchEnterTarget,
  isEnterKeyEvent,
} from "../shared/searchEnterTarget";
import { NutritionLibrarySkeletonGrid } from "../shared/NutritionLibrarySkeletonGrid";
import { useDesktopAutoFocusTextInput } from "../shared/useDesktopAutoFocusTextInput";
import { useLatchedEditorPersistedState } from "../shared/useLatchedEditorPersistedState";

const PLAN_MACRO_SORT_KEYS = {
  protein: NUTRITION_PLAN_SORT_KEYS.PROTEIN_DESC,
  carbs: NUTRITION_PLAN_SORT_KEYS.CARBS_DESC,
  fats: NUTRITION_PLAN_SORT_KEYS.FATS_DESC,
};

const PLAN_SORT_OPTIONS = [
  {
    key: NUTRITION_PLAN_SORT_KEYS.UPDATED_AT_DESC,
    labelKey: "lastUpdated-string",
  },
  {
    key: NUTRITION_PLAN_SORT_KEYS.NAME_ASC,
    labelKey: "name-string",
  },
  {
    key: NUTRITION_PLAN_SORT_KEYS.CALORIES_DESC,
    labelKey: "energy-string",
  },
  ...UI_MACRO_DISPLAY_ORDER.map((field) => ({
    key: PLAN_MACRO_SORT_KEYS[field],
    labelKey: `${field}-string`,
  })),
];

function getNutritionPlanCardKey(plan) {
  return String(plan?.id ?? "").trim().toLowerCase() || null;
}

function createEmptyNutritionPlan() {
  return {
    ...createCoachNutritionPlanSeed(),
  };
}

function getPendingNutritionPlanCardKey(planRecord, pendingUserId) {
  const normalizedPlanId = String(planRecord?.id ?? "").trim().toLowerCase();
  const normalizedPendingUserId = String(pendingUserId ?? "").trim().toLowerCase();
  return normalizedPlanId && normalizedPendingUserId
    ? `${normalizedPendingUserId}:${normalizedPlanId}`
    : null;
}

const NutritionPlanCard = React.memo(
  ({
    cardWidth,
    plan,
    t,
    onPress,
    onAssign,
    screenSize,
    isMobile,
    language,
    isEnterTarget = false,
    onHoverStart,
  }) => {
    const [isHovered, setIsHovered] = useState(false);
    const [isAssignHovered, setIsAssignHovered] = useState(false);
    const displayName = getNutritionPlanDisplayName(plan, language);
    const updatedDateLabel = formatNutritionPlanUpdatedDate(plan?.updatedAt, language);
    const updatedDateMetaLabel = updatedDateLabel
      ? t("nutritionPlanUpdatedDate-string", {
          defaultValue: "Updated {{date}}",
          date: updatedDateLabel,
        })
      : "";
    const macroValues = {
      protein: plan?.protein,
      carbs: plan?.carbs,
      fats: plan?.fats,
    };

    return (
      <View style={{ width: cardWidth }}>
        <TouchableOpacity
          activeOpacity={0.96}
          onPress={onPress}
          onMouseEnter={
            !isMobile
              ? () => {
                  setIsHovered(true);
                  onHoverStart?.();
                }
              : undefined
          }
          onMouseLeave={!isMobile ? () => setIsHovered(false) : undefined}
          style={styles.cardTouchable}
        >
          <View
            style={[
              styles.card,
              isHovered && styles.cardHovered,
              isEnterTarget && styles.cardEnterTarget,
            ]}
          >
            <View style={styles.cardMain}>
              <View style={styles.cardLeft}>
                <Text numberOfLines={1} style={styles.cardName}>
                  {displayName}
                </Text>
              </View>
              <View style={styles.cardRight}>
                <Text style={styles.cardKcal}>
                  {formatCalorieValue(plan?.calories)}
                  <Text style={styles.cardKcalUnit}> kcal</Text>
                </Text>
                <View style={styles.cardMacroRow}>
                  {UI_MACRO_DISPLAY_ORDER.map((key) => (
                    <Text key={key} style={styles.cardMacroValue}>
                      {getUiMacroShortLabel(key, language)}{" "}
                      <Text style={styles.cardMacroNum}>
                        {formatMacroValue(macroValues[key])}g
                      </Text>
                    </Text>
                  ))}
                </View>
              </View>
            </View>
            <View style={styles.cardFooter}>
              {updatedDateMetaLabel ? (
                <Text
                  ellipsizeMode="head"
                  numberOfLines={1}
                  style={styles.cardUpdatedDateText}
                >
                  {updatedDateMetaLabel}
                </Text>
              ) : null}
              <TouchableOpacity
                activeOpacity={0.82}
                onPress={(event) => {
                  event?.stopPropagation?.();
                  onAssign?.();
                }}
                onMouseEnter={!isMobile ? () => setIsAssignHovered(true) : undefined}
                onMouseLeave={!isMobile ? () => setIsAssignHovered(false) : undefined}
                style={styles.assignButtonTouchable}
              >
                <View
                  style={[
                    styles.assignButton,
                    isAssignHovered ? styles.assignButtonHovered : null,
                  ]}
                >
                  <Ionicons name="people-outline" size={13} color={Colors.darkgrayColor} />
                  <Text style={styles.assignButtonText}>
                    {t("nutritionPlanAssignAction-string", "Duplicate for athlete")}
                  </Text>
                </View>
              </TouchableOpacity>
            </View>
          </View>
        </TouchableOpacity>
      </View>
    );
  },
);

const PendingNutritionPlanCard = React.memo(function PendingNutritionPlanCard({
  cardWidth,
  invitationEmail,
  isMobile,
  language,
  menuItems,
  onMenuAction,
  rowUiState,
  planRecord,
}) {
  const { t } = useTranslation();
  const [isHovered, setIsHovered] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMenuTriggerHovered, setIsMenuTriggerHovered] = useState(false);
  const rowPressStartedWithMenuOpenRef = useRef(false);
  const totals = planRecord?.summary_totals ?? {};
  const displayName = getNutritionPlanDisplayName(planRecord, language);
  const updatedDateLabel = formatNutritionPlanUpdatedDate(planRecord?.updatedAt, language);
  const updatedDateMetaLabel = updatedDateLabel
    ? t("nutritionPlanUpdatedDate-string", {
        defaultValue: "Updated {{date}}",
        date: updatedDateLabel,
      })
    : "";
  const showMenuTrigger = rowUiState?.showMenuTrigger === true;
  const openMenuOnPress = rowUiState?.openMenuOnPress === true;
  const shouldFadeCard = rowUiState?.shouldFadeCard === true;

  useEffect(() => {
    if (!openMenuOnPress && isMenuOpen) {
      setIsMenuOpen(false);
    }
  }, [isMenuOpen, openMenuOnPress]);

  return (
    <View style={{ width: cardWidth }}>
      <TouchableOpacity
        activeOpacity={0.92}
        onPressIn={() => {
          if (!openMenuOnPress) return;
          rowPressStartedWithMenuOpenRef.current = Boolean(isMenuOpen);
        }}
        onPress={() => {
          if (!openMenuOnPress) return;
          const startedWithMenuOpen = rowPressStartedWithMenuOpenRef.current;
          rowPressStartedWithMenuOpenRef.current = false;
          setIsMenuOpen(!startedWithMenuOpen);
        }}
        onMouseEnter={() => !isMobile && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={[
          styles.pendingPlanCardTouchable,
          shouldFadeCard ? styles.pendingPlanCardOffline : null,
        ]}
      >
        <View
          style={[
            styles.pendingPlanCard,
            isHovered ? styles.pendingPlanCardHovered : null,
          ]}
        >
          <View style={styles.pendingPlanCardContent}>
            <View style={styles.pendingPlanCardHeader}>
              <View style={styles.pendingPlanTitleWrap}>
                <Text numberOfLines={1} style={styles.pendingPlanTitle}>
                  {displayName}
                </Text>
                {invitationEmail ? (
                  <Text numberOfLines={1} style={styles.pendingPlanInvitationEmail}>
                    {invitationEmail}
                  </Text>
                ) : null}
              </View>

              {showMenuTrigger ? (
                <FloatingMenu
                  placement="bottom-end"
                  width={220}
                  items={menuItems}
                  open={isMenuOpen}
                  onOpenChange={setIsMenuOpen}
                  triggerContainerStyle={styles.pendingPlanMenuTriggerWrap}
                  trigger={
                    <View
                      style={{
                        opacity: isMenuTriggerHovered ? 1 : 0.75,
                        transition: "opacity 150ms ease",
                      }}
                      onMouseEnter={() => !isMobile && setIsMenuTriggerHovered(true)}
                      onMouseLeave={() => setIsMenuTriggerHovered(false)}
                    >
                      <Entypo
                        name="dots-three-horizontal"
                        size={16}
                        color={
                          isMenuTriggerHovered ? Colors.darkgrayColor : Colors.grayColor
                        }
                      />
                    </View>
                  }
                  renderItem={({ item, close }) => (
                    <HoverableTouchableOpacity
                      disabled={item?.disabled === true}
                      style={[
                        styles.pendingPlanMenuItemRow,
                        item?.disabled === true ? styles.pendingPlanMenuItemDisabled : null,
                      ]}
                      hoverStyle={
                        item?.disabled === true ? null : styles.pendingPlanMenuItemHover
                      }
                      onPress={(event) => {
                        if (item?.disabled === true) {
                          return;
                        }
                        event?.stopPropagation?.();
                        onMenuAction?.(planRecord, item.key);
                        close();
                      }}
                      title={item.label}
                    >
                      {item?.icon?.type === "ionicons" ? (
                        <Ionicons
                          name={item.icon.name}
                          size={14}
                          color={item.isDanger ? Colors.redColor : Colors.darkgrayColor}
                        />
                      ) : null}
                      <Text
                        style={[
                          styles.pendingPlanMenuItemText,
                          item.isDanger ? styles.pendingPlanMenuItemDanger : null,
                        ]}
                      >
                        {item.label}
                      </Text>
                    </HoverableTouchableOpacity>
                  )}
                />
              ) : null}
            </View>

            <View style={styles.pendingPlanSummaryRow}>
              <Text style={styles.pendingPlanKcalValue}>
                {formatCalorieValue(totals?.calories)}
                <Text style={styles.pendingPlanKcalUnit}> kcal</Text>
              </Text>
              <View style={styles.pendingPlanMacroRow}>
                {UI_MACRO_DISPLAY_ORDER.map((key) => (
                  <Text key={key} style={styles.pendingPlanMacroValue}>
                    {getUiMacroShortLabel(key, language)}{" "}
                    <Text style={styles.pendingPlanMacroNum}>
                      {formatMacroValue(totals?.[key])}g
                    </Text>
                  </Text>
                ))}
              </View>
            </View>
          </View>

          <View style={styles.pendingPlanCardFooter}>
            {updatedDateMetaLabel ? (
              <Text
                ellipsizeMode="head"
                numberOfLines={1}
                style={styles.pendingPlanUpdatedDateText}
              >
                {updatedDateMetaLabel}
              </Text>
            ) : null}
          </View>
        </View>
      </TouchableOpacity>
    </View>
  );
});

export const NutritionPlansView = observer(function NutritionPlansView({ coach }) {
  const { t, i18n } = useTranslation();
  const { showToast } = useToast();
  const { screenSize } = useScreenDimensions();
  const { isMobile, isIOS } = useDevice();
  const [selectedSortKey, setSelectedSortKey] = useState(
    NUTRITION_PLAN_SORT_KEYS.UPDATED_AT_DESC,
  );
  const [isSortTriggerHovered, setIsSortTriggerHovered] = useState(false);
  const [isSortMenuOpen, setIsSortMenuOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [enterTargetOverrideKey, setEnterTargetOverrideKey] = useState(null);
  const [isCreateButtonHovered, setIsCreateButtonHovered] = useState(false);
  const [assigningPlanId, setAssigningPlanId] = useState(null);
  const [isPendingSectionCollapsed, setIsPendingSectionCollapsed] =
    useState(true);
  const [isPendingSectionHeaderHovered, setIsPendingSectionHeaderHovered] =
    useState(false);
  const [pendingActionByPlanKey, setPendingActionByPlanKey] = useState({});
  const [pendingDeleteTarget, setPendingDeleteTarget] = useState(null);
  const searchInputRef = useRef(null);
  const isSearchInputPressingRef = useRef(false);
  const pendingChevronSpin = useRef(new Animated.Value(0)).current;
  const sortChevronRotation = useChevronRotation(
    isSortMenuOpen,
    "closedDownOpenUp",
  );
  useDesktopAutoFocusTextInput(searchInputRef, { isMobile });
  const language = i18n?.resolvedLanguage || i18n?.language || "en";
  const editingPlanId =
    String(coach?.nutritionEditingPlanId ?? "").trim().toLowerCase() || null;
  const sortOptions = useMemo(
    () =>
      PLAN_SORT_OPTIONS.map((option) => ({
        ...option,
        label: t(option.labelKey),
      })),
    [t],
  );
  const selectedSortOption =
    sortOptions.find((option) => option.key === selectedSortKey) || sortOptions[0];
  const planCardLayout = getNutritionPlanLibraryCardLayout(screenSize);
  const coachPlans = Array.isArray(coach?.nutritionStore?.sortedPlans)
    ? coach.nutritionStore.sortedPlans
    : [];
  const pendingSectionEntries = selectPendingNutritionPlanEntries({
    pendingWorkspaceSummaries: coach?.pendingWorkspaceSummaries,
    clients: coach?.clients,
    invitationSummaries: coach?.invitationSummaries,
  });
  const pendingSectionPlans = useMemo(
    () => flattenPendingNutritionPlansFromEntries(pendingSectionEntries),
    [pendingSectionEntries],
  );
  const pendingSectionTotalCount = getPendingNutritionPlansTotalCount(
    pendingSectionEntries,
  );
  const shouldShowPendingSection = shouldShowPendingNutritionPlansSection(
    pendingSectionEntries,
  );
  const pendingChevronRotation = pendingChevronSpin.interpolate({
    inputRange: [0, 1],
    outputRange: ["180deg", "0deg"],
  });
  const shouldShowInitialSkeletons =
    coach?.nutritionStore?.isFetchingPlans === true &&
    coach?.nutritionStore?.hasFetchedPlans !== true;
  const currentEditorPlan = editingPlanId
    ? coach?.nutritionStore?.planEntitiesById?.get?.(editingPlanId) ?? null
    : null;
  const isEditingExistingPlan = useLatchedEditorPersistedState(
    editingPlanId,
    currentEditorPlan,
  );
  const allPlans = coachPlans;
  const assignmentPlan = assigningPlanId
    ? coach?.nutritionStore?.planEntitiesById?.get?.(assigningPlanId) ??
      allPlans.find((plan) => plan?.id === assigningPlanId) ??
      null
    : null;
  // Keep this derivation in observer render flow so MobX tracks the plan fields that
  // affect ordering and visibility, including same-array updatedAt changes.
  const filteredPlans = deriveVisibleNutritionPlans(allPlans, {
    searchTerm: searchText,
    sortKey: selectedSortKey,
    language,
  });
  const enterTargetPlan = useMemo(
    () =>
      getSearchEnterTarget({
        searchText,
        items: filteredPlans,
        getItemKey: getNutritionPlanCardKey,
        overrideKey: enterTargetOverrideKey,
      }),
    [enterTargetOverrideKey, filteredPlans, searchText],
  );
  const hasEnterTarget = enterTargetPlan != null;
  const enterTargetPlanKey = getNutritionPlanCardKey(enterTargetPlan);

  useEffect(() => {
    if (editingPlanId && !currentEditorPlan) {
      coach?.setProperty?.("nutritionEditingPlanId", null);
    }
  }, [coach, currentEditorPlan, editingPlanId]);

  useEffect(() => {
    setEnterTargetOverrideKey(null);
  }, [searchText]);

  useEffect(() => {
    if (!shouldShowPendingSection) {
      setIsPendingSectionCollapsed(true);
    }
  }, [shouldShowPendingSection]);

  useEffect(() => {
    Animated.timing(pendingChevronSpin, {
      toValue: isPendingSectionCollapsed ? 0 : 1,
      duration: 280,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    }).start();
  }, [isPendingSectionCollapsed, pendingChevronSpin]);

  const focusSearchInput = useCallback(() => {
    if (isSearchInputPressingRef.current) {
      return;
    }

    searchInputRef.current?.focus();
  }, []);
  const openCreatePlan = useCallback(() => {
    const nextPlan = coach?.nutritionStore?.createDraftPlan?.(
      createEmptyNutritionPlan(),
      { isLocalNewPlan: true },
    ) ?? createEmptyNutritionPlan();
    coach?.setProperty?.("nutritionEditingPlanId", nextPlan.id);
  }, [coach, language]);
  const openEditPlan = useCallback((plan) => {
    coach?.setProperty?.("nutritionEditingPlanId", plan?.id ?? null);
  }, [coach]);
  const openEnterTargetPlan = useCallback(() => {
    if (!enterTargetPlan) {
      return;
    }

    openEditPlan(enterTargetPlan);
  }, [enterTargetPlan, openEditPlan]);
  const closePlanEditor = useCallback(async () => {
    if (editingPlanId) {
      try {
        await coach?.nutritionStore?.finalizePlanEditorClose?.(editingPlanId);
      } catch (error) {
        console.error("Failed to finalize nutrition plan editor close:", error);
        showToast("nutritionPlanDeleteErrorTitle-string", {
          type: "error",
          title: "nutritionPlanDeleteErrorTitle-string",
          description: "nutritionCommonTryAgainLater-string",
        });
        return false;
      }
    }
    coach?.setProperty?.("nutritionEditingPlanId", null);
    return true;
  }, [coach, editingPlanId, showToast]);
  const handleDuplicatePlan = useCallback(() => {
    const planDraft = currentEditorPlan?.getDraftSnapshot?.();
    if (!coach?.nutritionStore || !planDraft) {
      return;
    }

    const nextPlan = coach.nutritionStore.createDraftPlan(
      buildNutritionPlanDuplicateDraft(planDraft, {
        language,
        namingSources: [
          currentEditorPlan,
          coach?.nutritionStore?.planRootRecordsById?.get?.(currentEditorPlan?.id),
        ],
      }),
      { isLocalNewPlan: true },
    );
    coach.nutritionStore.ensureEntityHasUnsyncedDraftForImmediateSave(nextPlan, [
      "name",
      "notes",
      "goals",
      "meals",
    ]);
    nextPlan.saveNow?.().catch((error) => {
      console.error("Failed to queue duplicated nutrition plan save:", error);
    });
    coach?.setProperty?.("nutritionEditingPlanId", nextPlan.id);
  }, [coach, currentEditorPlan, language]);
  const handleDeletePlan = useCallback(
    async (planEntity) => {
      const planId = planEntity?.id ?? currentEditorPlan?.id;
      if (!coach?.nutritionStore || !planId) {
        return;
      }

      const deletePlanName = getNutritionPlanDisplayName(
        planEntity ?? currentEditorPlan,
        language,
      );

      try {
        await coach.nutritionStore.deletePlan(planId);
        const didClose = await closePlanEditor();
        if (!didClose) {
          return;
        }
        showToast("nutritionPlanDeleteSuccessTitle-string", {
          type: "info",
          title: "nutritionPlanDeleteSuccessTitle-string",
          description: "nutritionPlanDeleteSuccessDescription-string",
          variables: {
            name: deletePlanName,
          },
        });
      } catch (error) {
        console.error("Failed to delete nutrition plan:", error);
        showToast("nutritionPlanDeleteErrorTitle-string", {
          type: "error",
          title: "nutritionPlanDeleteErrorTitle-string",
          description: "nutritionCommonTryAgainLater-string",
        });
        throw error;
      }
    },
    [closePlanEditor, coach, currentEditorPlan, language, showToast],
  );
  const openAssignPlan = useCallback((plan) => {
    setAssigningPlanId(plan?.id ?? null);
  }, []);
  const closeAssignPlan = useCallback(() => {
    setAssigningPlanId(null);
  }, []);
  const handleSearchKeyPress = useCallback(
    (event) => {
      if (!isEnterKeyEvent(event)) {
        return;
      }

      if (shouldShowInitialSkeletons || !hasEnterTarget) {
        return;
      }

      event?.preventDefault?.();
      openEnterTargetPlan();
    },
    [hasEnterTarget, openEnterTargetPlan, shouldShowInitialSkeletons],
  );

  const pendingMenuItems = useMemo(
    () => [
      {
        key: "moveToLibrary",
        label: t("moveToLibrary-string"),
        icon: { type: "ionicons", name: "book-outline" },
      },
      {
        key: "delete",
        label: t("delete-string"),
        icon: { type: "ionicons", name: "trash-outline" },
        isDanger: true,
      },
    ],
    [t],
  );

  const setPendingActionInFlight = useCallback((planKey, actionKey = null) => {
    const normalizedPlanKey = String(planKey ?? "").trim().toLowerCase();
    if (!normalizedPlanKey) {
      return;
    }

    setPendingActionByPlanKey((previousMap) => {
      if (!actionKey) {
        const { [normalizedPlanKey]: _ignored, ...rest } = previousMap;
        return rest;
      }

      return {
        ...previousMap,
        [normalizedPlanKey]: actionKey,
      };
    });
  }, []);

  const removePendingPlanFromLocalSummaries = useCallback(
    ({ pendingUserId, planId }) => {
      if (!coach) return;

      const nextSummaries = removePendingNutritionPlanFromWorkspaceSummaries({
        pendingWorkspaceSummaries: coach.pendingWorkspaceSummaries,
        pendingUserId,
        planId,
      });

      coach.setProperty("pendingWorkspaceSummaries", nextSummaries);
    },
    [coach],
  );

  const showPendingNutritionSyncErrorToast = useCallback(() => {
    showToast("syncErrorNutritionPlans-string", {
      type: "error",
      description: "syncErrorNutritionPlansDescription-string",
    });
  }, [showToast]);

  const refreshPendingWorkspaceSummaries = useCallback(
    (reason) => {
      if (
        coach &&
        typeof coach.refreshPendingWorkspaceSummariesFromCloud === "function"
      ) {
        void coach.refreshPendingWorkspaceSummariesFromCloud({ reason }).catch(
          (error) => {
            console.warn("[pending-workspace] nutrition_refresh_failed", {
              coachId: coach?.id,
              reason,
              error: error?.message ?? error,
            });
          },
        );
      }
    },
    [coach],
  );

  const handlePendingPlanDelete = useCallback(async () => {
    if (!pendingDeleteTarget?.planId || !pendingDeleteTarget?.pendingUserId) {
      return;
    }

    const pendingPlanKey = getPendingNutritionPlanCardKey(
      { id: pendingDeleteTarget.planId },
      pendingDeleteTarget.pendingUserId,
    );
    if (!pendingPlanKey) {
      setPendingDeleteTarget(null);
      return;
    }

    setPendingActionInFlight(pendingPlanKey, "delete");
    try {
      const deleteResult = await coach?.nutritionStore?.deletePendingNutritionPlan?.({
        pendingUserId: pendingDeleteTarget.pendingUserId,
        planId: pendingDeleteTarget.planId,
      });

      if (!deleteResult?.ok) {
        showPendingNutritionSyncErrorToast();
        return;
      }

      removePendingPlanFromLocalSummaries({
        pendingUserId: pendingDeleteTarget.pendingUserId,
        planId: pendingDeleteTarget.planId,
      });
      refreshPendingWorkspaceSummaries("pending_nutrition_plan_deleted");
      setPendingDeleteTarget(null);
    } finally {
      setPendingActionInFlight(pendingPlanKey, null);
    }
  }, [
    coach?.nutritionStore,
    pendingDeleteTarget,
    refreshPendingWorkspaceSummaries,
    removePendingPlanFromLocalSummaries,
    setPendingActionInFlight,
    showPendingNutritionSyncErrorToast,
  ]);

  const handlePendingPlanMenuAction = useCallback(
    async (planRecord, actionKey, pendingUserId) => {
      const pendingPlanKey = getPendingNutritionPlanCardKey(
        planRecord,
        pendingUserId,
      );
      if (!pendingPlanKey) {
        return;
      }

      const isActionDisabled = shouldDisablePendingNutritionMenuRowInteractions({
        isOffline: coach?.isOffline === true,
        isActionInFlight: pendingActionByPlanKey[pendingPlanKey] != null,
      });
      if (isActionDisabled) {
        return;
      }

      if (actionKey === "delete") {
        setPendingDeleteTarget({
          pendingUserId,
          planId: planRecord?.id,
          planRecord,
        });
        return;
      }

      if (actionKey !== "moveToLibrary") {
        return;
      }

      setPendingActionInFlight(pendingPlanKey, "moveToLibrary");
      try {
        const moveResult =
          await coach?.nutritionStore?.movePendingNutritionPlanToLibrary?.({
            pendingUserId,
            planId: planRecord?.id,
            fallbackPlanRecord: planRecord,
            language,
            showToast,
          });

        if (!moveResult?.ok) {
          showPendingNutritionSyncErrorToast();
          return;
        }

        removePendingPlanFromLocalSummaries({
          pendingUserId,
          planId: planRecord?.id,
        });
        refreshPendingWorkspaceSummaries("pending_nutrition_plan_moved_to_library");
      } finally {
        setPendingActionInFlight(pendingPlanKey, null);
      }
    },
    [
      coach,
      language,
      pendingActionByPlanKey,
      refreshPendingWorkspaceSummaries,
      removePendingPlanFromLocalSummaries,
      setPendingActionInFlight,
      showPendingNutritionSyncErrorToast,
      showToast,
    ],
  );

  if (currentEditorPlan) {
    return (
      <>
        <NutritionPlanAssignmentModal
          isVisible={Boolean(assignmentPlan)}
          coach={coach}
          plan={assignmentPlan}
          onClose={closeAssignPlan}
        />
        <NutritionPlanEditorView
          coach={coach}
          planEntity={currentEditorPlan}
          isEditing={isEditingExistingPlan}
          onBack={closePlanEditor}
          onAssign={openAssignPlan}
          onDuplicate={handleDuplicatePlan}
          onDelete={handleDeletePlan}
        />
      </>
    );
  }

  return (
    <View style={styles.container}>
      <NutritionPlanAssignmentModal
        isVisible={Boolean(assignmentPlan)}
        coach={coach}
        plan={assignmentPlan}
        onClose={closeAssignPlan}
      />
      <View style={styles.headerCard}>
        <View style={styles.searchRow}>
          <TouchableOpacity
            activeOpacity={1}
            onPress={focusSearchInput}
            style={styles.searchField}
            accessible={true}
            accessibilityRole="button"
            accessibilityLabel={t("searchByName-string")}
            aria-label={t("searchByName-string")}
          >
            <View style={styles.searchIcon}>
              <Octicons name="search" size={14} color={Colors.grayColor} />
            </View>
            <TextInput
              ref={searchInputRef}
              value={searchText}
              onChangeText={setSearchText}
              onKeyPress={handleSearchKeyPress}
              onPressIn={() => {
                isSearchInputPressingRef.current = true;
              }}
              onPressOut={() => {
                isSearchInputPressingRef.current = false;
              }}
              onBlur={() => {
                isSearchInputPressingRef.current = false;
              }}
              placeholder={t("searchByName-string")}
              placeholderTextColor={Colors.grayColor}
              style={[
                styles.searchInput,
                isMobile && isIOS ? styles.searchInputIOS : null,
              ]}
              accessibilityLabel={t("searchByName-string")}
              aria-label={t("searchByName-string")}
            />
          </TouchableOpacity>
          <TouchableOpacity
            activeOpacity={0.8}
            onPress={() => {
              setIsCreateButtonHovered(false);
              openCreatePlan();
            }}
            onMouseEnter={!isMobile ? () => setIsCreateButtonHovered(true) : undefined}
            onMouseLeave={!isMobile ? () => setIsCreateButtonHovered(false) : undefined}
          >
            <View
              style={[
                styles.createButton,
                isCreateButtonHovered ? styles.createButtonHovered : null,
              ]}
            >
              <AntDesign name="plus" size={12} color={Colors.whiteColor} />
              <Text style={styles.createButtonText}>{t("create-string")}</Text>
            </View>
          </TouchableOpacity>
        </View>
        <View style={styles.divider} />
        <View style={styles.bottomRow}>
          <FloatingMenu
            placement="bottom-end"
            width={220}
            items={sortOptions}
            open={isSortMenuOpen}
            onOpenChange={setIsSortMenuOpen}
            trigger={
              <View
                style={styles.sortTrigger}
                onMouseEnter={!isMobile ? () => setIsSortTriggerHovered(true) : undefined}
                onMouseLeave={!isMobile ? () => setIsSortTriggerHovered(false) : undefined}
              >
                <MaterialIcons name="sort" size={14} color={Colors.darkgrayColor} />
                <Text
                  style={[
                    styles.sortTriggerValue,
                    isSortTriggerHovered ? styles.sortTriggerLabelHovered : null,
                  ]}
                >
                  {selectedSortOption?.label ?? t("sortBy-string")}
                </Text>
                <Animated.View
                  style={{ transform: [{ rotate: sortChevronRotation }] }}
                >
                  <Ionicons
                    name="chevron-down"
                    size={14}
                    color={
                      isSortTriggerHovered ? Colors.darkgrayColor : Colors.grayColor
                    }
                  />
                </Animated.View>
              </View>
            }
            renderItem={({ item, close }) => (
              <HoverableTouchableOpacity
                style={styles.sortMenuItem}
                hoverStyle={styles.sortMenuItemHover}
                onPress={() => {
                  setSelectedSortKey(item.key);
                  close();
                }}
              >
                <Text
                  style={[
                    styles.sortMenuItemText,
                    item.key === selectedSortKey ? styles.sortMenuItemTextActive : null,
                  ]}
                >
                  {item.label}
                </Text>
                {item.key === selectedSortKey ? (
                  <Ionicons name="checkmark" size={15} color={Colors.bubbleColor} />
                ) : null}
              </HoverableTouchableOpacity>
            )}
          />
        </View>
      </View>

      <View style={styles.results}>
        {shouldShowPendingSection ? (
          <View style={styles.pendingSectionContainer}>
            <TouchableOpacity
              onPress={() =>
                setIsPendingSectionCollapsed((isCollapsed) => !isCollapsed)
              }
              style={[
                styles.pendingSectionHeader,
                isPendingSectionHeaderHovered
                  ? styles.pendingSectionHeaderHovered
                  : null,
              ]}
              onMouseEnter={() =>
                !isMobile && setIsPendingSectionHeaderHovered(true)
              }
              onMouseLeave={() => setIsPendingSectionHeaderHovered(false)}
            >
              <View style={styles.pendingSectionHeaderLeft}>
                <Ionicons
                  name="folder-outline"
                  size={20}
                  color={Colors.grayColor}
                />
                <Text numberOfLines={1} style={styles.pendingSectionTitle}>
                  {t("pendingUserNutritionPlansSectionTitle-string")}
                </Text>
                <View style={styles.pendingSectionCountChip}>
                  <Text style={styles.pendingSectionCountChipText}>
                    {pendingSectionTotalCount}
                  </Text>
                </View>
              </View>
              <Animated.View
                style={[
                  styles.pendingSectionChevronWrap,
                  { transform: [{ rotate: pendingChevronRotation }] },
                ]}
              >
                <Ionicons
                  name="chevron-down"
                  size={16}
                  color={Colors.darkgrayColor}
                />
              </Animated.View>
            </TouchableOpacity>

            {!isPendingSectionCollapsed ? (
              <View
                style={[
                  styles.pendingSectionCardsGrid,
                  { gap: planCardLayout.gap },
                ]}
              >
                {pendingSectionPlans.map((pendingPlanEntry) => {
                  const pendingPlanKey = getPendingNutritionPlanCardKey(
                    pendingPlanEntry.planRecord,
                    pendingPlanEntry.pendingUserId,
                  );
                  const isActionInFlight =
                    pendingPlanKey &&
                    pendingActionByPlanKey[pendingPlanKey] != null;
                  const rowUiState = resolvePendingNutritionPlanRowUiState({
                    isOffline: coach?.isOffline === true,
                    isActionInFlight,
                  });
                  const menuItems = pendingMenuItems.map((item) => ({
                    ...item,
                    disabled: rowUiState.isDisabled,
                  }));

                  return (
                    <PendingNutritionPlanCard
                      cardWidth={planCardLayout.cardWidth}
                      key={pendingPlanEntry.id}
                      invitationEmail={pendingPlanEntry.invitationEmail}
                      isMobile={isMobile}
                      language={language}
                      menuItems={menuItems}
                      onMenuAction={(planRecord, actionKey) =>
                        handlePendingPlanMenuAction(
                          planRecord,
                          actionKey,
                          pendingPlanEntry.pendingUserId,
                        )
                      }
                      rowUiState={rowUiState}
                      planRecord={pendingPlanEntry.planRecord}
                    />
                  );
                })}
              </View>
            ) : null}
          </View>
        ) : null}

        {shouldShowInitialSkeletons ? (
          <NutritionLibrarySkeletonGrid screenSize={screenSize} variant="plan" />
        ) : allPlans.length === 0 ? (
          <IconEmptyView
            iconLibrary="Ionicons"
            iconName="reader-outline"
            mainText="nutritionPlanLibraryEmptyTitle-string"
            subText="nutritionPlanLibraryEmptyDescription-string"
            marginTop={140}
            marginBottom={12}
          />
        ) : filteredPlans.length === 0 ? (
          <IconEmptyView
            iconLibrary="Ionicons"
            iconName="search-outline"
            mainText="noNutritionPlansFound-string"
            subText="adjustFiltersOrSearch-string"
            marginTop={140}
            marginBottom={12}
          />
        ) : (
          <View
            style={[
              styles.foodCardGrid,
              { gap: planCardLayout.gap },
            ]}
          >
            {filteredPlans.map((plan) => (
              <NutritionPlanCard
                key={plan.id}
                cardWidth={planCardLayout.cardWidth}
                plan={plan}
                t={t}
                screenSize={screenSize}
                isMobile={isMobile}
                language={language}
                isEnterTarget={
                  hasEnterTarget && getNutritionPlanCardKey(plan) === enterTargetPlanKey
                }
                onHoverStart={() =>
                  setEnterTargetOverrideKey(getNutritionPlanCardKey(plan))
                }
                onPress={() => openEditPlan(plan)}
                onAssign={() => openAssignPlan(plan)}
              />
            ))}
          </View>
        )}
      </View>
      <CustomAlert
        isVisible={Boolean(pendingDeleteTarget)}
        onClose={() => setPendingDeleteTarget(null)}
        title="deletePendingNutritionPlanAlertTitle-string"
        text="deletePendingNutritionPlanAlertText-string"
        textParams={{
          itemName: getNutritionPlanDisplayName(
            pendingDeleteTarget?.planRecord,
            language,
          ),
        }}
        action={handlePendingPlanDelete}
        specialCTA="delete-string"
      />
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    width: "100%",
    marginTop: 24,
  },
  headerCard: {
    backgroundColor: Colors.whiteColor,
    borderRadius: 12,
    borderWidth: 0.5,
    borderColor: Colors.borderColor,
  },
  searchRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 14,
    minHeight: 50,
    paddingVertical: 10,
    gap: 10,
  },
  searchField: {
    flex: 1,
    minWidth: 0,
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 10,
    cursor: "text",
  },
  searchIcon: {
    justifyContent: "center",
    alignItems: "center",
  },
  searchInput: {
    flex: 1,
    marginLeft: 10,
    fontSize: 14,
    fontWeight: "400",
    color: Colors.blackColor,
    outlineStyle: "none",
  },
  searchInputIOS: {
    fontSize: 16,
  },
  divider: {
    height: 0.5,
    backgroundColor: Colors.borderColor,
    marginHorizontal: 14,
  },
  bottomRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    paddingVertical: 10,
    paddingHorizontal: 14,
    gap: 12,
  },
  createButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: Colors.bubbleColor,
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 10,
    flexShrink: 0,
    transition: "opacity 180ms ease-in-out",
  },
  createButtonHovered: {
    opacity: 0.6,
  },
  createButtonText: {
    ...Fonts.medium13,
    color: Colors.whiteColor,
    marginLeft: 6,
  },
  sortTrigger: {
    flexDirection: "row",
    alignItems: "center",
    gap: 7,
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 8,
    flexShrink: 0,
  },
  sortTriggerLabelHovered: {
    color: Colors.darkgrayColor,
  },
  sortTriggerValue: {
    fontSize: 12,
    fontWeight: "500",
    color: Colors.darkgrayColor,
  },
  sortMenuItem: {
    width: "100%",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    paddingHorizontal: 10,
    paddingVertical: 10,
    borderRadius: 8,
  },
  sortMenuItemHover: {
    backgroundColor: Colors.hoveredGray,
  },
  sortMenuItemText: {
    fontSize: 12,
    fontWeight: "400",
    color: Colors.darkgrayColor,
    flexShrink: 1,
  },
  sortMenuItemTextActive: {
    fontWeight: "500",
    color: Colors.bubbleColor,
  },
  results: {
    marginTop: 24,
    marginBottom: 36,
  },
  pendingSectionContainer: {
    borderRadius: 14,
    backgroundColor: "transparent",
    marginBottom: 12,
    overflow: "hidden",
  },
  pendingSectionHeader: {
    minHeight: 44,
    paddingHorizontal: 8,
    paddingVertical: 8,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  pendingSectionHeaderHovered: {
    opacity: 0.72,
  },
  pendingSectionHeaderLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    flex: 1,
    minWidth: 0,
  },
  pendingSectionTitle: {
    fontSize: 14,
    lineHeight: 20,
    fontWeight: "400",
    color: Colors.blackColor,
    letterSpacing: 0.2,
    flexShrink: 1,
  },
  pendingSectionCountChip: {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: "rgba(30, 41, 59, 0.05)",
    alignItems: "center",
    justifyContent: "center",
  },
  pendingSectionCountChipText: {
    fontSize: 10,
    fontWeight: "600",
    color: Colors.darkgrayColor,
  },
  pendingSectionChevronWrap: {
    width: 24,
    height: 24,
    alignItems: "center",
    justifyContent: "center",
  },
  pendingSectionCardsGrid: {
    paddingTop: 4,
    paddingBottom: 14,
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
  },
  foodCardGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
  },
  pendingPlanCardTouchable: {
    outlineStyle: "none",
    outlineWidth: 0,
    outlineColor: "transparent",
    boxShadow: "none",
    width: "100%",
  },
  pendingPlanCard: {
    borderRadius: 12,
    backgroundColor: Colors.whiteColor,
    borderWidth: 0.1,
    borderColor: Colors.borderColor,
    overflow: "hidden",
    transitionProperty: "border-color, background-color",
    transitionDuration: "200ms",
    transitionTimingFunction: "ease-in-out",
  },
  pendingPlanCardHovered: {
    borderColor: Colors.grayColor,
    backgroundColor: "#FDFDFD",
  },
  pendingPlanCardOffline: {
    opacity: 0.5,
  },
  pendingPlanCardContent: {
    paddingVertical: 14,
    paddingHorizontal: 14,
    gap: 10,
  },
  pendingPlanCardHeader: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
  },
  pendingPlanTitleWrap: {
    flex: 1,
    minWidth: 0,
  },
  pendingPlanTitle: {
    color: Colors.blackColor,
    fontSize: 14,
    fontWeight: "500",
    lineHeight: 20,
  },
  pendingPlanInvitationEmail: {
    color: Colors.grayColor,
    fontSize: 11,
    lineHeight: 16,
    marginTop: 4,
  },
  pendingPlanMenuTriggerWrap: {
    width: 28,
    height: 28,
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
  },
  pendingPlanSummaryRow: {
    alignItems: "flex-end",
    flexDirection: "row",
    gap: 8,
    justifyContent: "space-between",
  },
  pendingPlanKcalValue: {
    color: Colors.darkgrayColor,
    fontSize: 13,
    fontWeight: "600",
    lineHeight: 16,
  },
  pendingPlanKcalUnit: {
    color: Colors.grayColor,
    fontSize: 11,
    fontWeight: "400",
  },
  pendingPlanMacroRow: {
    alignItems: "center",
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 10,
    justifyContent: "flex-end",
  },
  pendingPlanMacroValue: {
    color: Colors.grayColor,
    fontSize: 11,
    fontWeight: "400",
    letterSpacing: 0.2,
    lineHeight: 16,
  },
  pendingPlanMacroNum: {
    color: Colors.grayColor,
    fontSize: 11,
    fontWeight: "400",
  },
  pendingPlanCardFooter: {
    borderTopWidth: 0.5,
    borderTopColor: Colors.borderColor,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  pendingPlanUpdatedDateText: {
    fontSize: 10,
    fontWeight: "400",
    fontStyle: "italic",
    color: Colors.grayColor,
    lineHeight: 14,
  },
  pendingPlanMenuItemRow: {
    width: "100%",
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingHorizontal: 10,
    paddingVertical: 10,
    borderRadius: 8,
  },
  pendingPlanMenuItemHover: {
    backgroundColor: Colors.hoveredGray,
  },
  pendingPlanMenuItemDisabled: {
    opacity: 0.48,
  },
  pendingPlanMenuItemText: {
    fontSize: 12,
    color: Colors.blackColor,
    fontWeight: "500",
  },
  pendingPlanMenuItemDanger: {
    color: Colors.redColor,
  },
  cardTouchable: {
    outlineStyle: "none",
    outlineWidth: 0,
    outlineColor: "transparent",
    boxShadow: "none",
    width: "100%",
  },
  cardMain: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 16,
    paddingHorizontal: 18,
  },
  cardFooter: {
    alignItems: "center",
    borderTopWidth: 0.5,
    borderTopColor: Colors.borderColor,
    flexDirection: "row",
    justifyContent: "space-between",
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  cardUpdatedDateText: {
    fontSize: 10,
    fontWeight: "400",
    fontStyle: "italic",
    color: Colors.grayColor,
    flexShrink: 1,
    lineHeight: 14,
  },
  assignButtonTouchable: {
    marginLeft: "auto",
  },
  assignButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingHorizontal: 8,
    paddingVertical: 6,
    borderRadius: 8,
  },
  assignButtonHovered: {
    backgroundColor: Colors.hoveredGray,
  },
  assignButtonText: {
    fontSize: 11,
    fontWeight: "500",
    color: Colors.darkgrayColor,
    letterSpacing: 0.2,
  },
  card: {
    borderRadius: 12,
    outlineStyle: "none",
    outlineWidth: 0,
    outlineColor: "transparent",
    backgroundColor: Colors.whiteColor,
    borderWidth: 0.1,
    borderColor: Colors.borderColor,
    overflow: "hidden",
    transitionProperty: "border-color, background-color",
    transitionDuration: "200ms",
    transitionTimingFunction: "ease-in-out",
  },
  cardHovered: {
    borderColor: Colors.grayColor,
    backgroundColor: "#FDFDFD",
  },
  cardEnterTarget: {
    borderColor: Colors.darkgrayColor,
    backgroundColor: "#FDFDFD",
  },
  cardLeft: {
    flex: 1,
    minWidth: 0,
    marginRight: 16,
  },
  cardName: {
    fontSize: 14,
    fontWeight: "500",
    lineHeight: 20,
    color: Colors.blackColor,
  },
  cardDatabase: {
    fontSize: 10,
    lineHeight: 14,
    color: Colors.grayColor,
    letterSpacing: 0.3,
    textTransform: "uppercase",
    marginTop: 3,
  },
  cardRight: {
    alignItems: "flex-end",
    gap: 4,
  },
  cardKcal: {
    fontSize: 13,
    fontWeight: "600",
    color: Colors.darkgrayColor,
  },
  cardKcalUnit: {
    fontSize: 11,
    fontWeight: "400",
    color: Colors.grayColor,
  },
  cardMacroRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  cardMacroValue: {
    fontSize: 11,
    fontWeight: "400",
    letterSpacing: 0.2,
    color: Colors.grayColor,
  },
  cardMacroNum: {
    fontSize: 11,
    fontWeight: "400",
    color: Colors.grayColor,
  },
});
